import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from 'src/lib/utils';
import { IconBadgeVerified, IconBadgeValidated, IconBadgeCertified } from 'src/components/Svg/Images';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { EscrowStatusEnum } from 'src/graphql/schema';

type BadgeProps = {
  title: string;
  description: string;
  icon: React.ElementType;
  isActive?: boolean;
  onClick: () => void;
};

const Badge = ({ title, description, icon: Icon, isActive = false, onClick }: BadgeProps) => {
  return (
    <div
      className={cn(
        'rounded-lg shadow-sm p-4 text-center',
        isActive ? 'bg-secondary-foreground cursor-pointer' : 'bg-muted',
      )}
      onClick={isActive ? onClick : undefined}
    >
      <div className="size-10 flex items-center justify-center mx-auto mb-3">
        <Icon className={cn('size-8 opacity-30', isActive && 'size-10 opacity-100')} />
      </div>
      <div className={cn('text-muted-foreground space-y-1.5', isActive && 'text-white')}>
        <div className="text-sm font-bold">{title}</div>
        <p className="text-xs whitespace-pre-wrap">{description}</p>
      </div>
    </div>
  );
};

type Props = {
  className?: string;
  ref: React.RefObject<HTMLDivElement | null>;
};

const VerificationBadges = ({ className, ref }: Props) => {
  const {
    softwareEscrow: { status, activatedAt, certificationVerified, certificationCertified },
  } = useSoftwareEscrow();

  const isTerminated = status === EscrowStatusEnum.Terminated;

  const onClick = () => {
    if (ref?.current && !isTerminated) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Card className={cn('flex flex-col', isTerminated && 'opacity-70 pointer-events-none', className)}>
      <CardHeader className="pb-4">
        <CardTitle className="text-lg">Your trust level</CardTitle>
        <CardDescription>
          Verification badges mark the trust, security, and credibility of your escrowed material.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-[7px]">
          <Badge
            title="Validated"
            description={`Material in\nescrow`}
            icon={IconBadgeValidated}
            isActive={Boolean(activatedAt)}
            onClick={onClick}
          />
          <Badge
            title="Verified"
            description={`Extra checks\nperformed`}
            icon={IconBadgeVerified}
            isActive={certificationVerified}
            onClick={onClick}
          />
          <Badge
            title="Certified"
            description={`Expert\nreviewed`}
            icon={IconBadgeCertified}
            isActive={certificationCertified}
            onClick={onClick}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default VerificationBadges;
